<script>
/* global Sentry */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import PipelinesAPI from 'dashboard/api/pipelines';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { getStageColorClasses } from '../pipeline/components/pipelineTemplates';

export default {
  name: 'PipelineSection',
  components: { ConfirmationModal, NextSelect },
  data() {
    return {
      // NOOVI (cliente 2026-07-03): uma conversa/lead pode estar em VÁRIOS
      // pipelines ao mesmo tempo. Guardamos TODOS os cards vinculados, não só o
      // primeiro — o painel lista cada card com seus próprios controles.
      pipelineCards: [],
      pipelines: [],
      loading: false,
      loadError: false,
      fetchRequestId: 0,
      showAddForm: false,
      selectedPipelineId: null,
      selectedStage: null,
      creatingCard: false,
      mutatingCardId: null,
      confirmingCardId: null,
      mutationGeneration: 0,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentAccountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      currentCustomRoleId: 'getCurrentCustomRoleId',
    }),
    canDeletePipelineCards() {
      return this.currentRole === 'administrator' && !this.currentCustomRoleId;
    },
    currentConversationId() {
      // In the frontend store, 'id' IS the display_id (conversations are indexed by display_id)
      return this.currentChat?.id || null;
    },
    pipelineContext() {
      return {
        accountId: this.currentAccountId || null,
        conversationId: this.currentConversationId,
      };
    },
    hasPipelineCards() {
      return this.pipelineCards.length > 0;
    },
    selectedPipeline() {
      if (!this.selectedPipelineId || !this.pipelines.length) return null;
      return this.pipelines.find(f => f.id === this.selectedPipelineId);
    },
    // Pipelines em que a conversa AINDA não tem card. Evita criar um segundo
    // card no mesmo pipeline (o backend não tem restrição de unicidade).
    availablePipelines() {
      const linkedIds = new Set(this.pipelineCards.map(c => c.pipeline_id));
      return this.pipelines.filter(p => !linkedIds.has(p.id));
    },
    contact() {
      return this.currentChat?.meta?.sender || {};
    },
  },
  watch: {
    pipelineContext: {
      handler() {
        this.invalidateMutationContext();
        this.fetchPipelineData();
      },
      immediate: true,
    },
  },
  methods: {
    invalidateMutationContext() {
      this.mutationGeneration += 1;
      if (this.confirmingCardId !== null) {
        this.$refs.removeConfirmationRef?.cancel?.();
      }
      this.creatingCard = false;
      this.mutatingCardId = null;
      this.confirmingCardId = null;
    },
    captureMutationContext() {
      return {
        accountId: this.currentAccountId,
        conversationId: this.currentConversationId,
        generation: this.mutationGeneration,
      };
    },
    isCurrentMutationContext({ accountId, conversationId, generation }) {
      return (
        generation === this.mutationGeneration &&
        String(accountId) === String(this.currentAccountId) &&
        String(conversationId) === String(this.currentConversationId)
      );
    },
    isCurrentFetch(requestId, accountId, conversationId) {
      return (
        requestId === this.fetchRequestId &&
        String(accountId) === String(this.currentAccountId) &&
        String(conversationId) === String(this.currentConversationId)
      );
    },
    async fetchPipelineData() {
      const accountId = this.currentAccountId;
      const conversationId = this.currentConversationId;
      this.fetchRequestId += 1;
      const requestId = this.fetchRequestId;

      // Never keep actionable cards from the previous conversation on screen.
      this.pipelineCards = [];
      this.pipelines = [];
      this.showAddForm = false;
      this.loadError = false;

      if (!accountId || !conversationId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const [pipelinesResponse, pipelineCardResponse] = await Promise.all([
          PipelinesAPI.get(accountId),
          PipelineCardsAPI.getCardByConversationId(conversationId, {
            accountId,
          }),
        ]);

        if (!this.isCurrentFetch(requestId, accountId, conversationId)) return;
        this.pipelines = pipelinesResponse.data || [];
        this.pipelineCards = pipelineCardResponse.data || [];
      } catch (error) {
        if (!this.isCurrentFetch(requestId, accountId, conversationId)) return;
        this.loadError = true;
        // eslint-disable-next-line no-console
        console.error('Error fetching pipeline data:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
      } finally {
        if (this.isCurrentFetch(requestId, accountId, conversationId)) {
          this.loading = false;
        }
      }
    },
    pipelineForCard(card) {
      if (!card?.pipeline_id || !this.pipelines.length) return null;
      return this.pipelines.find(p => p.id === card.pipeline_id);
    },
    stageLabelForCard(card) {
      const pipeline = this.pipelineForCard(card);
      if (!pipeline?.stages) return card?.pipeline_stage || null;
      return pipeline.stages[card.pipeline_stage]?.name || card.pipeline_stage;
    },
    stageColorForCard(card) {
      const pipeline = this.pipelineForCard(card);
      return pipeline?.stages?.[card.pipeline_stage]?.color || '#64748B';
    },
    stageBadgeClass(card) {
      return getStageColorClasses(this.stageColorForCard(card)).badge;
    },
    pipelineOptions(pipelines = this.pipelines) {
      return pipelines.map(pipeline => ({
        value: pipeline.id,
        label: pipeline.name,
      }));
    },
    stageOptions(pipeline) {
      return this.sortedStages(pipeline).map(stage => ({
        value: stage.id,
        label: stage.name,
      }));
    },
    sortedStages(pipeline) {
      return Object.entries(pipeline?.stages || {})
        .map(([id, stage]) => ({ id, ...stage }))
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    },
    generatePipelineSlug(pipeline) {
      if (!pipeline) return '';
      const name = pipeline.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      return `${pipeline.id}-${name}`;
    },
    goToPipeline(card) {
      const pipeline = this.pipelineForCard(card);
      if (!card || !pipeline) return;

      this.$router.push({
        name: 'pipeline_item_detail',
        params: {
          accountId: this.$route.params.accountId,
          pipelineSlug: this.generatePipelineSlug(pipeline),
          view: 'item',
          itemId: card.id,
        },
      });
    },
    async moveToStage(card, newStage) {
      if (
        !card ||
        this.mutatingCardId !== null ||
        newStage === card.pipeline_stage
      ) {
        return;
      }

      this.mutatingCardId = card.id;
      try {
        const updatedCard = await PipelineCardsAPI.moveToStage(
          card.id,
          newStage
        );
        this.replaceCard(
          card.id,
          updatedCard || { ...card, pipeline_stage: newStage }
        );
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.MOVED_SUCCESS'));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error moving to stage:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.MOVED_ERROR'));
      } finally {
        this.mutatingCardId = null;
      }
    },
    getFirstStageByPosition(stages) {
      const stagesObj = stages || {};
      const sortedStages = Object.entries(stagesObj)
        .map(([key, value]) => ({ key, ...value }))
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      return (
        sortedStages.find(s => !s.is_won_stage && !s.is_lost_stage)?.key ||
        sortedStages[0]?.key ||
        'lead'
      );
    },
    async changePipeline(card, newPipelineId) {
      const pipelineId = Number(newPipelineId);
      if (
        !card ||
        this.mutatingCardId !== null ||
        pipelineId === card.pipeline_id
      ) {
        return;
      }

      const newPipeline = this.pipelines.find(p => p.id === pipelineId);
      if (!newPipeline) return;

      const firstStage = this.getFirstStageByPosition(newPipeline.stages);

      this.mutatingCardId = card.id;
      try {
        await PipelineCardsAPI.updateCard(card.id, {
          pipeline_id: pipelineId,
          pipeline_stage: firstStage,
        });
        this.replaceCard(card.id, {
          ...card,
          pipeline_id: pipelineId,
          pipeline_stage: firstStage,
        });
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(
          this.$t('CONTACT_PANEL.PIPELINE_SECTION.PIPELINE_CHANGED_SUCCESS')
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error changing pipeline:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
        useAlert(
          this.$t('CONTACT_PANEL.PIPELINE_SECTION.PIPELINE_CHANGED_ERROR')
        );
      } finally {
        this.mutatingCardId = null;
      }
    },
    async removeFromPipeline(card) {
      if (
        !this.canDeletePipelineCards ||
        !card ||
        !this.currentAccountId ||
        !this.currentConversationId ||
        this.mutatingCardId !== null ||
        this.confirmingCardId !== null
      ) {
        return;
      }

      const cardId = card.id;
      const mutationContext = this.captureMutationContext();
      this.confirmingCardId = cardId;
      const confirmed =
        await this.$refs.removeConfirmationRef.showConfirmation();
      if (!this.isCurrentMutationContext(mutationContext)) return;
      if (this.confirmingCardId === cardId) {
        this.confirmingCardId = null;
      }
      if (
        !confirmed ||
        this.mutatingCardId !== null ||
        !this.isCurrentMutationContext(mutationContext)
      ) {
        return;
      }

      this.mutatingCardId = cardId;
      try {
        if (!this.isCurrentMutationContext(mutationContext)) return;
        await PipelineCardsAPI.deleteCard(cardId, {
          accountId: mutationContext.accountId,
          currentCard: card,
        });
        if (!this.isCurrentMutationContext(mutationContext)) return;
        this.pipelineCards = this.pipelineCards.filter(c => c.id !== cardId);
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVED_SUCCESS'));
      } catch (error) {
        if (!this.isCurrentMutationContext(mutationContext)) return;
        // eslint-disable-next-line no-console
        console.error('Error removing from pipeline:', error);
        if (window.Sentry) {
          Sentry.captureException(error);
        }
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVED_ERROR'));
      } finally {
        if (
          this.isCurrentMutationContext(mutationContext) &&
          this.mutatingCardId === cardId
        ) {
          this.mutatingCardId = null;
        }
      }
    },
    replaceCard(cardId, updatedCard) {
      const index = this.pipelineCards.findIndex(c => c.id === cardId);
      if (index !== -1) {
        this.pipelineCards.splice(index, 1, updatedCard);
      }
    },
    toggleAddForm() {
      this.showAddForm = !this.showAddForm;
      if (this.showAddForm && this.availablePipelines.length > 0) {
        this.selectedPipelineId = this.availablePipelines[0].id;
        this.updateDefaultStage();
      }
    },
    updateDefaultStage() {
      if (this.selectedPipeline?.stages) {
        this.selectedStage = this.getFirstStageByPosition(
          this.selectedPipeline.stages
        );
      }
    },
    async createPipelineCard() {
      if (
        this.creatingCard ||
        !this.selectedPipelineId ||
        !this.selectedStage ||
        !this.currentConversationId ||
        !this.currentAccountId
      )
        return;

      const mutationContext = this.captureMutationContext();
      const pipelineId = this.selectedPipelineId;
      const pipelineStage = this.selectedStage;
      const conversationTitle =
        this.contact?.name ||
        this.$t('CONTACT_PANEL.PIPELINE_SECTION.NEW_CONVERSATION');
      this.creatingCard = true;
      try {
        const cardData = {
          pipeline_id: pipelineId,
          pipeline_stage: pipelineStage,
          conversation_display_id: mutationContext.conversationId,
          item_details: {
            title: conversationTitle,
            conversation_id: mutationContext.conversationId,
          },
        };

        if (!this.isCurrentMutationContext(mutationContext)) return;
        const response = await PipelineCardsAPI.createCard(cardData, {
          accountId: mutationContext.accountId,
        });
        if (!this.isCurrentMutationContext(mutationContext)) return;
        if (response.data) {
          this.pipelineCards.push(response.data);
        }
        this.showAddForm = false;
        // Invalidate pipeline cards cache so Pipeline board shows updated data
        this.$store.dispatch('pipelineCards/itemUpdated');
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.ADDED_SUCCESS'));
      } catch (error) {
        if (!this.isCurrentMutationContext(mutationContext)) return;
        // eslint-disable-next-line no-console
        console.error('Error creating pipeline card:', error);
        // Send to Sentry
        if (window.Sentry) {
          Sentry.withScope(scope => {
            scope.setContext('pipeline_card', {
              pipeline_id: pipelineId,
              stage: pipelineStage,
              conversation_id: mutationContext.conversationId,
            });
            scope.setExtra('error_response', error.response?.data);
            Sentry.captureException(error);
          });
        }
        useAlert(this.$t('CONTACT_PANEL.PIPELINE_SECTION.ADDED_ERROR'));
      } finally {
        if (this.isCurrentMutationContext(mutationContext)) {
          this.creatingCard = false;
        }
      }
    },
  },
};
</script>

<template>
  <div class="py-2">
    <!-- Loading state -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-4"
      role="status"
      aria-live="polite"
    >
      <span
        class="i-lucide-loader-2 motion-safe:animate-spin text-n-slate-10"
        aria-hidden="true"
      />
      <span class="sr-only">{{
        $t('CONTACT_PANEL.PIPELINE_SECTION.LOADING')
      }}</span>
    </div>

    <div
      v-else-if="loadError"
      class="flex flex-col items-start gap-2 py-3"
      role="alert"
    >
      <p class="m-0 text-sm text-n-ruby-11">
        {{ $t('CONTACT_PANEL.PIPELINE_SECTION.LOAD_ERROR') }}
      </p>
      <button
        type="button"
        class="inline-flex min-h-11 items-center rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="fetchPipelineData"
      >
        {{ $t('CONTACT_PANEL.PIPELINE_SECTION.RETRY') }}
      </button>
    </div>

    <!-- Pipeline cards (0..N) + shared add form -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="card in pipelineCards"
        :key="card.id"
        class="flex flex-col gap-3 pb-3 border-0 border-b border-solid border-n-weak last:border-b-0 last:pb-0"
      >
        <!-- Pipeline and Stage badges -->
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="px-2 py-1 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11"
          >
            {{
              pipelineForCard(card)?.name ||
              $t('CONTACT_PANEL.PIPELINE_SECTION.NO_PIPELINE')
            }}
          </span>
          <span
            v-if="stageLabelForCard(card)"
            class="rounded-full border px-2 py-1 text-xs font-medium"
            :class="stageBadgeClass(card)"
          >
            {{ stageLabelForCard(card) }}
          </span>
        </div>

        <!-- Change Pipeline (if multiple pipelines) -->
        <div v-if="pipelines.length > 1" class="flex flex-col gap-1">
          <label
            :for="`pipeline-card-${card.id}-pipeline`"
            class="text-xs text-n-slate-10"
          >
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.CHANGE_PIPELINE') }}
          </label>
          <NextSelect
            :input-id="`pipeline-card-${card.id}-pipeline`"
            class="w-full [&>select]:w-full"
            :model-value="card.pipeline_id"
            :options="pipelineOptions()"
            :aria-label="$t('CONTACT_PANEL.PIPELINE_SECTION.CHANGE_PIPELINE')"
            :disabled="mutatingCardId !== null"
            @update:model-value="changePipeline(card, $event)"
          />
        </div>

        <!-- Move to stage -->
        <div v-if="pipelineForCard(card)?.stages" class="flex flex-col gap-1">
          <label
            :for="`pipeline-card-${card.id}-stage`"
            class="text-xs text-n-slate-10"
          >
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.MOVE_STAGE') }}
          </label>
          <NextSelect
            :input-id="`pipeline-card-${card.id}-stage`"
            class="w-full [&>select]:w-full"
            :model-value="card.pipeline_stage"
            :options="stageOptions(pipelineForCard(card))"
            :aria-label="$t('CONTACT_PANEL.PIPELINE_SECTION.MOVE_STAGE')"
            :disabled="mutatingCardId !== null"
            @update:model-value="moveToStage(card, $event)"
          />
        </div>

        <!-- Action buttons -->
        <div class="flex gap-1.5 mt-1">
          <button
            type="button"
            class="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border-0 px-3 text-sm text-n-slate-11 outline outline-1 outline-n-strong transition-colors duration-200 hover:bg-n-slate-9/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="goToPipeline(card)"
          >
            <span class="i-lucide-external-link flex-shrink-0" />
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.VIEW_ITEM') }}
          </button>
          <button
            v-if="canDeletePipelineCards"
            v-tooltip.top="$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
            type="button"
            :aria-label="$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
            :disabled="mutatingCardId !== null || confirmingCardId !== null"
            class="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border-0 text-n-ruby-11 outline outline-1 outline-n-ruby-8 transition-colors duration-200 hover:bg-n-ruby-9/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="removeFromPipeline(card)"
          >
            <span class="i-lucide-trash-2 flex-shrink-0" aria-hidden="true" />
          </button>
        </div>
      </div>

      <!-- Empty hint (no cards, not adding) -->
      <p
        v-if="!hasPipelineCards && !showAddForm"
        class="text-xs text-n-slate-10 m-0"
      >
        {{ $t('CONTACT_PANEL.PIPELINE_SECTION.NO_ITEM') }}
      </p>

      <!-- Add button: first card OR another pipeline (shared). Hidden when the
           conversation already has a card in every pipeline. -->
      <button
        v-if="!showAddForm && availablePipelines.length > 0"
        type="button"
        class="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border-0 px-3 text-sm text-n-slate-11 outline outline-1 outline-n-strong transition-colors duration-200 hover:bg-n-slate-9/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="toggleAddForm"
      >
        <span class="i-lucide-plus flex-shrink-0" />
        {{
          hasPipelineCards
            ? $t('CONTACT_PANEL.PIPELINE_SECTION.ADD_ANOTHER_PIPELINE')
            : $t('CONTACT_PANEL.PIPELINE_SECTION.ADD_TO_PIPELINE')
        }}
      </button>
      <p
        v-else-if="!showAddForm && !pipelines.length"
        class="text-xs text-n-slate-10 m-0"
      >
        {{ $t('CONTACT_PANEL.PIPELINE_SECTION.NO_PIPELINES_AVAILABLE') }}
      </p>

      <!-- Add form (shared: first card or additional pipeline) -->
      <div v-if="showAddForm" class="flex flex-col gap-3">
        <!-- Select Pipeline -->
        <div class="flex flex-col gap-1">
          <label
            for="pipeline-section-new-pipeline"
            class="text-xs text-n-slate-10"
          >
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_PIPELINE') }}
          </label>
          <NextSelect
            v-model="selectedPipelineId"
            input-id="pipeline-section-new-pipeline"
            class="w-full [&>select]:w-full"
            :options="pipelineOptions(availablePipelines)"
            :aria-label="$t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_PIPELINE')"
            @update:model-value="updateDefaultStage"
          />
        </div>

        <!-- Select Stage -->
        <div v-if="selectedPipeline?.stages" class="flex flex-col gap-1">
          <label
            for="pipeline-section-new-stage"
            class="text-xs text-n-slate-10"
          >
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_STAGE') }}
          </label>
          <NextSelect
            v-model="selectedStage"
            input-id="pipeline-section-new-stage"
            class="w-full [&>select]:w-full"
            :options="stageOptions(selectedPipeline)"
            :aria-label="$t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_STAGE')"
          />
        </div>

        <!-- Buttons -->
        <div class="flex gap-1.5">
          <button
            type="button"
            class="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg border-0 px-3 text-sm text-n-slate-11 outline outline-1 outline-n-strong transition-colors duration-200 hover:bg-n-slate-9/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="toggleAddForm"
          >
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.CANCEL') }}
          </button>
          <button
            type="button"
            class="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border-0 bg-n-brand px-3 text-sm text-white transition-[color,background-color,filter] duration-200 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
            :disabled="!selectedPipelineId || !selectedStage || creatingCard"
            @click="createPipelineCard"
          >
            <span
              v-if="creatingCard"
              class="i-lucide-loader-2 motion-safe:animate-spin"
              aria-hidden="true"
            />
            <span v-else class="i-lucide-check" aria-hidden="true" />
            {{ $t('CONTACT_PANEL.PIPELINE_SECTION.ADD') }}
          </button>
        </div>
      </div>
    </div>

    <ConfirmationModal
      ref="removeConfirmationRef"
      :title="$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
      :description="$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE_CONFIRM')"
      :confirm-label="$t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
      :cancel-label="$t('CONTACT_PANEL.PIPELINE_SECTION.CANCEL')"
    />
  </div>
</template>
